import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Grid from "@/config/Grid/Grid";
import Button from "@/components/Button/Button";
import IconButton from "@/components/IconButton/IconButton";
import Counter from "@/components/Counter/Counter";
import NoteField from "@/components/NoteField/NoteField";
import ToppingTile from "@/components/ToppingTile/ToppingTile";
import DishDetails from "@/components/DishDetails/DishDetails";
import TransparentButton from "@/components/TransparentButton/TransparentButton";
import { useMenuData } from "@/hooks/useMenuData";
import { useDishReference } from "@/hooks/useDishReference";
import {
  resolveSizesForDish,
  resolveToppingsForDish,
} from "@/helpers/dishOptions";
import {
  getTranslation,
  getTranslatedDescription,
} from "@/helpers/getTranslation";
import { formatGrosze, lineUnitPriceGrosze } from "@/helpers/cartPricing";
import { useCartActions } from "@/store/useCartStore";
import { orderPaths } from "@/routes/paths";
import { useGoBack } from "@/hooks/useGoBack";
import type { CartTopping } from "@/types/order";
import OrderActionBar from "./components/OrderActionBar";
import styles from "./Order.module.scss";

function DishCustomizePage() {
  const {
    restaurantId = "",
    tableId = "",
    dishId = "",
  } = useParams<{ restaurantId: string; tableId: string; dishId: string }>();
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const paths = orderPaths(restaurantId, tableId);
  const goBack = useGoBack(paths.root);

  const { dishes, toppings, sizes, loading, error } = useMenuData(restaurantId);
  const reference = useDishReference(restaurantId);
  const { addLine } = useCartActions();

  const dish = useMemo(
    () => dishes.find((candidate) => candidate.id === dishId),
    [dishes, dishId],
  );

  const availableSizes = useMemo(
    () => (dish ? resolveSizesForDish(dish, sizes) : []),
    [dish, sizes],
  );
  const availableToppings = useMemo(
    () => (dish ? resolveToppingsForDish(dish, toppings) : []),
    [dish, toppings],
  );

  const [selectedSizeId, setSelectedSizeId] = useState<string | null>(null);
  const [toppingQuantities, setToppingQuantities] = useState<
    Record<string, number>
  >({});
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState("");

  const selectedSize = availableSizes.find(
    (size) => size.id === selectedSizeId,
  );
  const selectedToppings: CartTopping[] = availableToppings
    .map((topping) => ({
      ...topping,
      quantity: toppingQuantities[topping.id] ?? 0,
    }))
    .filter((topping) => topping.quantity > 0);

  const draftLine = dish
    ? {
        dishId: dish.id,
        translations: dish.translations,
        basePrice: dish.price,
        imageUrl: dish.image?.url,
        size: selectedSize,
        toppings: selectedToppings,
        extraNote: note.trim() || undefined,
        quantity,
      }
    : null;

  const unitPrice = draftLine
    ? lineUnitPriceGrosze({ ...draftLine, lineId: "draft" })
    : 0;
  const totalPrice = unitPrice * quantity;

  /**
   * Start on the first size the restaurant lists rather than on nothing. A
   * dish with sizes has no price until one is chosen, and a disabled button
   * with no obvious cause reads as broken.
   */
  useEffect(() => {
    if (!selectedSizeId && availableSizes.length > 0) {
      setSelectedSizeId(availableSizes[0].id);
    }
  }, [availableSizes, selectedSizeId]);

  // Still guarded, for the render between the sizes arriving and the pick.
  const mustPickSize = availableSizes.length > 0 && !selectedSize;

  const setToppingQuantity = (toppingId: string, quantity: number) =>
    setToppingQuantities((current) => ({ ...current, [toppingId]: quantity }));

  const handleAddToCart = () => {
    if (!draftLine || mustPickSize) return;
    addLine(draftLine);
    // Back to the category the guest was browsing, not the top level.
    goBack();
  };

  return (
    <>
      <Grid>
        <div className={`start-1 col-4 ${styles.orderWrapper}`}>
          <div className={styles.orderHeaderContainer}>
            <div className={styles.orderHeader}>
              <IconButton
                iconName="las la-angle-left icon-big"
                onClick={goBack}
                color="primary"
                variant="text"
              />
              <p className={`body-bold-large ${styles.orderTitle}`}>
                {dish ? getTranslation(dish.translations, language) : ""}
              </p>
              <IconButton
                iconName=""
                onClick={() => {}}
                color="primary"
                variant="text"
              />
            </div>
          </div>

          <div className={styles.orderContent}>
            {!dish && !loading ? (
              <p className={`body-medium ${styles.orderStateMessage}`}>
                {error ? t("order.loadError") : t("order.cart.unavailable")}
              </p>
            ) : null}

            {dish ? (
              <div className={styles.orderSection}>
                {dish.image?.url ? (
                  <img
                    src={dish.image.url}
                    alt={getTranslation(dish.translations, language)}
                    className={styles.dishHeroImage}
                  />
                ) : null}

                <DishDetails
                  dish={dish}
                  reference={reference}
                  language={language}
                  only="facts"
                />

                <p className={`body-medium ${styles.dishDescription}`}>
                  {getTranslatedDescription(dish.translations, language)}
                </p>

                <DishDetails
                  dish={dish}
                  reference={reference}
                  language={language}
                  only="sections"
                />

                {availableSizes.length > 0 ? (
                  <div className={styles.dishOptionGroup}>
                    <p className={`title-medium ${styles.dishOptionTitle}`}>
                      {t("order.dish.size")}
                    </p>
                    <div className={styles.dishSizeRow}>
                      {availableSizes.map((size) => (
                        <TransparentButton
                          key={size.id}
                          text={getTranslation(size.translations, language)}
                          value={size.id}
                          selectedValue={selectedSizeId ?? ""}
                          setValue={() => setSelectedSizeId(size.id)}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}

                {availableToppings.length > 0 ? (
                  <div className={styles.dishOptionGroup}>
                    <p className={`title-medium ${styles.dishOptionTitle}`}>
                      {t("order.dish.extras")}
                    </p>
                    {availableToppings.map((topping) => (
                      <ToppingTile
                        key={topping.id}
                        name={getTranslation(topping.translations, language)}
                        price={`+ ${formatGrosze(topping.price)}`}
                        quantity={toppingQuantities[topping.id] ?? 0}
                        onQuantityChange={(quantity) =>
                          setToppingQuantity(topping.id, quantity)
                        }
                      />
                    ))}
                  </div>
                ) : null}

                <div className={styles.dishOptionGroup}>
                  <NoteField
                    value={note}
                    onChange={setNote}
                    label={t("order.dish.note")}
                    placeholder={t("order.dish.notePlaceholder")}
                  />
                </div>

                <div className={styles.dishOptionGroup}>
                  <p className={`title-medium ${styles.dishOptionTitle}`}>
                    {t("order.dish.quantity")}
                  </p>
                  <Counter value={quantity} onChange={setQuantity} />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </Grid>

      {dish ? (
        <OrderActionBar>
          <Button
            onClick={handleAddToCart}
            label={`${t("order.button.addToCart")} · ${formatGrosze(totalPrice)}`}
            variant="contained"
            color="primary"
            maxWidth
            mobile={true}
            disabled={mustPickSize}
          />
        </OrderActionBar>
      ) : null}
    </>
  );
}

export default DishCustomizePage;
